import React, { useMemo } from "react";
import { Folder, Identifiable } from "@planzone/dto";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import styles from "./styles.scss";
import { Props } from "./types";
import { ArrowIcon } from "./arrowIcon";
import { FolderIcon } from "@common/folderIcon";
import { useSelectDocumentsContext } from "../../context";

function inPath(path: Identifiable<Folder>[], folder: Identifiable<Folder>) {
  return path.find(({ uuid }) => uuid === folder.uuid);
}

function DirectoryStructure({
  isRoot,
  folder,
  selectedFolderId,
  selectedFolderPath,
}: Props) {
  const [isOpen, setIsOpen] = React.useState(isRoot);

  const { t } = useTranslation();
  const { setCurrentFolder } = useSelectDocumentsContext();

  const childFolders = useMemo(
    () =>
      folder.children.filter(
        ({ type }) => type === "folder",
      ) as Identifiable<Folder>[],
    [folder],
  );
  const isSelected = useMemo(
    () => selectedFolderId === folder.uuid,
    [selectedFolderId, folder],
  );
  const isInPath = useMemo(
    () => inPath(selectedFolderPath, folder),
    [selectedFolderId, folder],
  );

  const onClickFolder = (folderUuid: string) => {
    setCurrentFolder(folderUuid);
  };

  return (
    <div
      className={classNames(styles.directoryStructure, {
        [styles.root]: isRoot,
        [styles.open]: isOpen,
        [styles.selected]: isSelected,
      })}
    >
      <div className={styles.header}>
        <div
          className={styles.toggleIconBox}
          onClick={() => setIsOpen(!isOpen)}
        >
          <ArrowIcon className={styles.toggleIcon} />
        </div>
        <FolderIcon className={styles.icon} />
        <h4
          className={styles.name}
          onClick={() => {
            onClickFolder(folder.uuid);
          }}
        >
          {isRoot ? t("projectPage.documentsPage.sideBar.folder") : folder.name}
        </h4>
      </div>
      {(isOpen || isSelected || isInPath) && (
        <div className={styles.children}>
          {childFolders.map((child) => (
            <div key={child.name} className={styles.child}>
              <DirectoryStructure
                folder={child}
                selectedFolderId={selectedFolderId}
                selectedFolderPath={selectedFolderPath}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { DirectoryStructure };
